import { useMemo, useState } from "react";
import {
  getConsent,
  getDefaultConsentPreferences,
  setConsent as setStoredConsent,
  type ConsentPreferences,
  type ConsentState,
} from "@/lib/utils/consent";

interface CookieConsentManagerState {
  consent: ConsentState | null;
  isPreferencesOpen: boolean;
  currentPreferences: ConsentPreferences;
  setIsPreferencesOpen: (open: boolean) => void;
  handleAcceptAll: () => void;
  handleRejectAll: () => void;
  handleSaveCustom: (preferences: ConsentPreferences) => void;
}

export function useCookieConsentManager(): CookieConsentManagerState {
  const [consent, setConsent] = useState<ConsentState | null>(() => getConsent());
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  const currentPreferences = useMemo(
    () => consent?.preferences ?? getDefaultConsentPreferences(),
    [consent]
  );

  const applyConsent = (status: ConsentState["status"], preferences: ConsentPreferences): void => {
    const nextConsent = setStoredConsent({ status, preferences });
    setConsent(nextConsent);
    setIsPreferencesOpen(false);
  };

  const handleAcceptAll = (): void => {
    applyConsent("accepted_all", {
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = (): void => {
    applyConsent("rejected_all", {
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSaveCustom = (preferences: ConsentPreferences): void => {
    applyConsent("custom", preferences);
  };

  return {
    consent,
    isPreferencesOpen,
    currentPreferences,
    setIsPreferencesOpen,
    handleAcceptAll,
    handleRejectAll,
    handleSaveCustom,
  };
}
